import React, { useState } from 'react';
import { Device } from '../../lib/types';
import Modal, { ModalTitle, ModalActions } from '../../components/Modal/Modal';
import Button from '../../components/Button/Button';
import styles from './Devices.module.scss';

interface NotRecoveredModalProps {
  open: boolean;
  onClose: () => void;
  device: Device | null;
  onConfirm: (device: Device) => Promise<void>;
}

export default function NotRecoveredModal({ open, onClose, device, onConfirm }: NotRecoveredModalProps) {
  const [submitting, setSubmitting] = useState(false);

  if (!device) {
    return null;
  }

  const currentDevice = device;

  async function handleConfirm() {
    if (submitting) {
      return;
    }
    setSubmitting(true);
    try {
      // Await the mutation so a failure keeps the modal open for retry — the
      // error toast is raised by the handler in Devices, which re-throws.
      await onConfirm(currentDevice);
      onClose();
    } catch {
      // Kept open on failure; toast already surfaced the error.
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal open={open} onClose={submitting ? () => {} : onClose}>
      <ModalTitle>Mark as not recovered</ModalTitle>
      <p className={styles.modalDescription}>
        Confirm that device <b>{device.id}</b>
        {device.petName ? ` (${device.petName})` : ''} is not coming back
        {device.adopterName ? ` from ${device.adopterName}` : ''}. This closes the transfer as failed and removes it
        from your list. We&rsquo;ll log it for reporting.
      </p>
      <div className={`${styles.outcomeBox} ${styles.outcomeBoxError}`}>
        <div className={styles.outcomeBoxIconError}>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </svg>
        </div>
        <div>
          <div className={styles.outcomeBoxLabel}>What happens</div>
          <div className={styles.outcomeBoxText}>
            Transfer closed as <b>failed &middot; device not recovered</b> &middot; removed from Stalled.
          </div>
        </div>
      </div>
      <ModalActions>
        <Button variant="ghost" onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="danger" onClick={handleConfirm} disabled={submitting}>
          {submitting ? 'Working…' : 'Confirm — not recovered'}
        </Button>
      </ModalActions>
    </Modal>
  );
}
